import React, { useMemo } from 'react';
import './CuadranteDataGrid.scss';
import festivosData from '../../../data/festivosEspana.json';

const CuadranteDataGrid = ({ cuadrantes, año, mes, tipoConsulta }) => {
  const mesesNombres = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Obtener el número de días del mes
  const diasDelMes = useMemo(() => {
    return new Date(año, mes, 0).getDate();
  }, [año, mes]);

  // Verificar si una fecha es festiva según el calendario de España
  const esFechaFestiva = useMemo(() => {
    const festivosDelAño = festivosData.festivosNacionales[año] || [];
    const festivosSet = new Set(festivosDelAño);
    
    return (dia) => {
      const fechaStr = `${año}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      return festivosSet.has(fechaStr);
    };
  }, [año, mes]);

  // Obtener día del campo CUADD_DD (para cuadrantes-detalles)
  const obtenerDia = (cuadrante) => {
    // El campo CUADD_DD contiene el día del mes
    if (cuadrante.CUADD_DD) {
      return parseInt(cuadrante.CUADD_DD);
    }
    return null;
  };

  // Detectar tipo de datos (cuadrantes-detalles o cuadrantes)
  const esCuadrantesDetalles = cuadrantes.length > 0 && cuadrantes[0].CUADD_DD !== undefined;

  // Procesar datos según tipo de consulta
  const datosProcessados = useMemo(() => {
    console.log('🔍 CuadranteDataGrid - Procesando datos:', {
      numCuadrantes: cuadrantes.length,
      tipoConsulta,
      esCuadrantesDetalles,
      primerCuadrante: cuadrantes[0]
    });

    if (tipoConsulta === 'contrato') {
      // BÚSQUEDA POR CONTRATO/SERVICIO → Mostrar personas (vigilantes)
      // Con la función combinada, ahora tenemos información de personas
      const porPersona = {};
      
      cuadrantes.forEach(cuadrante => {
        // Usar CUADT_PERSONA si está disponible, sino agrupar por turno
        const personaId = cuadrante.CUADT_PERSONA 
          ? `${cuadrante.CUADT_PERSONA}_${cuadrante.CUADD_TURNOPER}`
          : `Turno_${cuadrante.CUADD_TURNOPER}`;
        
        if (!porPersona[personaId]) {
          porPersona[personaId] = {
            persona: cuadrante.CUADT_PERSONA || personaId,
            nombre: cuadrante.VSSPER_NOM 
              ? `${cuadrante.VSSPER_NOM} ${cuadrante.VSSPER_APELL1} ${cuadrante.VSSPER_APELL2}`.trim()
              : `Turno ${cuadrante.CUADD_TURNOPER}`,
            turnosPorDia: {}, // { dia: { turno, horas, tipo } }
            totalHoras: 0,
            horasFestivas: 0,
            horasNocturnas: 0,
            contrato: cuadrante.CUADD_CTRT,
            servicio: cuadrante.CUADD_SERV,
            numeroTurno: cuadrante.CUADD_TURNOPER,
            nif: cuadrante.VSSPER_NIF ? `${cuadrante.VSSPER_NIF}${cuadrante.VSSPER_NIF_DC || ''}` : null
          };
        }

        // Obtener el día del mes
        const dia = obtenerDia(cuadrante);
        if (dia && dia >= 1 && dia <= diasDelMes) {
          const horas = parseFloat(cuadrante.CUADD_HHTOTD) || 0;
          const horasFest = parseFloat(cuadrante.CUADD_HFEST) || 0;
          const horasNoct = parseFloat(cuadrante.CUADD_HHNOCD) || 0;
          // Verificar festivo según el calendario real de España
          const esFestivo = esFechaFestiva(dia);
          
          porPersona[personaId].turnosPorDia[dia] = {
            turno: cuadrante.CUADD_TURNOPER,
            horas: horas,
            horaInicio: cuadrante.CUADD_HHINI,
            horaFin: cuadrante.CUADD_HHFIN,
            festivo: esFestivo,
            nocturno: horasNoct > 0,
            horasFestivas: esFestivo ? horas : 0 // Solo contar si realmente es festivo
          };
          
          // Agregar a totales
          porPersona[personaId].totalHoras += horas;
          // Solo contar horas festivas si el día realmente es festivo
          porPersona[personaId].horasFestivas += esFestivo ? horas : 0;
          porPersona[personaId].horasNocturnas += horasNoct;
        }
      });

      const resultado = Object.values(porPersona).sort((a, b) => {
        // Ordenar por nombre si tienen persona, sino por número de turno
        if (a.nombre && b.nombre) {
          return a.nombre.localeCompare(b.nombre);
        }
        return (a.numeroTurno || 0) - (b.numeroTurno || 0);
      });
      console.log('✅ Datos procesados (por persona):', resultado);
      return resultado;
    } else {
      // BÚSQUEDA POR PERSONA → Mostrar contratos/servicios
      // Agrupar por contrato/servicio
      const porContrato = {};
      
      cuadrantes.forEach(cuadrante => {
        const contratoId = `${cuadrante.CUADD_CTRT}/${cuadrante.CUADD_SERV}`;
        
        if (!porContrato[contratoId]) {
          porContrato[contratoId] = {
            contrato: cuadrante.CUADD_CTRT,
            servicio: cuadrante.CUADD_SERV,
            contratoServicio: contratoId,
            turnosPorDia: {},
            totalHoras: 0,
            horasFestivas: 0,
            horasNocturnas: 0,
            nombre: cuadrante.VSSPER_NOM 
              ? `${cuadrante.VSSPER_NOM} ${cuadrante.VSSPER_APELL1} ${cuadrante.VSSPER_APELL2}`.trim()
              : `Contrato ${contratoId}`
          };
        }

        // Obtener el día del mes
        const dia = obtenerDia(cuadrante);
        if (dia && dia >= 1 && dia <= diasDelMes) {
          const horas = parseFloat(cuadrante.CUADD_HHTOTD) || 0;
          const horasFest = parseFloat(cuadrante.CUADD_HFEST) || 0;
          const horasNoct = parseFloat(cuadrante.CUADD_HHNOCD) || 0;
          // Verificar festivo según el calendario real de España
          const esFestivo = esFechaFestiva(dia);
          
          porContrato[contratoId].turnosPorDia[dia] = {
            turno: cuadrante.CUADD_TURNOPER,
            horas: horas,
            horaInicio: cuadrante.CUADD_HHINI,
            horaFin: cuadrante.CUADD_HHFIN,
            festivo: esFestivo,
            nocturno: horasNoct > 0,
            horasFestivas: esFestivo ? horas : 0
          };
          
          // Agregar a totales
          porContrato[contratoId].totalHoras += horas;
          // Solo contar horas festivas si el día realmente es festivo
          porContrato[contratoId].horasFestivas += esFestivo ? horas : 0;
          porContrato[contratoId].horasNocturnas += horasNoct;
        }
      });

      const resultado = Object.values(porContrato);
      console.log('✅ Datos procesados (por contrato):', resultado);
      return resultado;
    }
  }, [cuadrantes, tipoConsulta, diasDelMes]);

  // Verificar si es fin de semana
  const esFinDeSemana = (dia) => {
    const fecha = new Date(año, mes - 1, dia);
    const diaSemana = fecha.getDay();
    return diaSemana === 0 || diaSemana === 6; // Domingo o Sábado
  };

  // Día de la semana para mostrar en cabecera (0=Dom, 1=Lun, ...)
  const nombresDiaSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const obtenerDiaSemana = (dia) => {
    const fecha = new Date(año, mes - 1, dia);
    return nombresDiaSemana[fecha.getDay()];
  };

  if (!cuadrantes || cuadrantes.length === 0) {
    return (
      <div className="cuadrante-datagrid-empty">
        <p>No hay cuadrantes para mostrar</p>
      </div>
    );
  }

  // Si no son cuadrantes-detalles, no podemos mostrar el DataGrid por días
  if (!esCuadrantesDetalles) {
    return (
      <div className="cuadrante-datagrid-empty">
        <p>⚠️ El DataGrid requiere datos con días específicos</p>
        <p>Para ver el desglose por días, busca por Contrato/Servicio</p>
        <p>O cambia a "Vista Lista" para ver el resumen mensual</p>
      </div>
    );
  }

  return (
    <div className="cuadrante-datagrid">
      <div className="datagrid-header">
        <h2>📊 {mesesNombres[mes - 1]} {año}</h2>
        <div className="legend">
          <span className="legend-item">
            <span className="legend-dot festivo"></span>
            Festivo Nacional
          </span>
          <span className="legend-item">
            <span className="legend-dot nocturno"></span>
            Nocturno
          </span>
          <span className="legend-item">
            <span className="legend-dot fin-semana"></span>
            Fin de semana
          </span>
        </div>
      </div>

      <div className="datagrid-container">
        <table className="datagrid-table">
          <thead>
            <tr>
              {/* Primera columna: Vigilante o Contrato/Servicio */}
              <th className="sticky-col header-col">
                {tipoConsulta === 'contrato' ? 'Vigilante' : 'Contrato/Servicio'}
              </th>
              
              {/* Columnas de días: número y día de la semana encima de cada columna de celdas */}
              {Array.from({ length: diasDelMes }, (_, i) => i + 1).map(dia => (
                <th 
                  key={dia} 
                  className={`dia-header ${esFinDeSemana(dia) ? 'fin-semana' : ''}`}
                  title={`${dia} ${obtenerDiaSemana(dia)}`}
                  scope="col"
                >
                  <div className="dia-header-inner">
                    <span className="dia-numero">{dia}</span>
                    <span className="dia-semana">{obtenerDiaSemana(dia)}</span>
                  </div>
                </th>
              ))}
              
              {/* Columnas de totales */}
              <th className="total-col">Total H</th>
              <th className="total-col">H Fest</th>
              <th className="total-col">H Noct</th>
            </tr>
          </thead>
          <tbody>
            {datosProcessados.map((fila, index) => (
              <tr key={index}>
                {/* Primera columna */}
                <td className="sticky-col nombre-col">
                  {tipoConsulta === 'contrato' ? (
                    <div className="persona-info">
                      <div className="persona-nombre">{fila.nombre}</div>
                      <div className="persona-id">
                        {fila.nif ? `NIF: ${fila.nif}` : `ID: ${fila.persona}`}
                        {` • T${fila.numeroTurno}`}
                      </div>
                    </div>
                  ) : (
                    <div className="contrato-info">
                      <div className="contrato-id">{fila.contratoServicio}</div>
                      <div className="contrato-nombre">{fila.nombre}</div>
                    </div>
                  )}
                </td>
                
                {/* Columnas de días */}
                {Array.from({ length: diasDelMes }, (_, i) => i + 1).map(dia => {
                  const turnoData = fila.turnosPorDia[dia];
                  const esFinde = esFinDeSemana(dia);
                  const esFestivo = esFechaFestiva(dia);
                  
                  return (
                    <td 
                      key={dia}
                      className={`dia-cell ${esFestivo ? 'festivo' : ''} ${esFinde ? 'fin-semana' : ''} ${turnoData ? 'con-turno' : 'sin-turno'}`}
                      title={esFestivo ? `Festivo: ${dia}/${mes}/${año}` : ''}
                    >
                      {turnoData && (
                        <div className="turno-cell">
                          <div className="turno-horario" title={`${turnoData.horaInicio} - ${turnoData.horaFin}`}>
                            <span className="turno-inicio">{turnoData.horaInicio}</span>
                            <span className="turno-fin">{turnoData.horaFin}</span>
                          </div>
                          <div className="turno-horas">
                            {turnoData.horas}h
                          </div>
                          {turnoData.festivo && <span className="badge festivo">F</span>}
                          {turnoData.nocturno && <span className="badge nocturno">N</span>}
                        </div>
                      )}
                    </td>
                  );
                })}
                
                {/* Columnas de totales */}
                <td className="total-col total-value">{fila.totalHoras.toFixed(2)}h</td>
                <td className="total-col total-value festivo">{fila.horasFestivas.toFixed(2)}h</td>
                <td className="total-col total-value nocturno">{fila.horasNocturnas.toFixed(2)}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resumen general */}
      <div className="datagrid-footer">
        <div className="resumen-general">
          <h4>📈 Resumen General</h4>
          <div className="resumen-items">
            <div className="resumen-item">
              <span className="label">Total Registros:</span>
              <span className="value">{datosProcessados.length}</span>
            </div>
            <div className="resumen-item">
              <span className="label">Total Horas:</span>
              <span className="value">
                {datosProcessados.reduce((sum, fila) => sum + fila.totalHoras, 0).toFixed(2)}h
              </span>
            </div>
            <div className="resumen-item">
              <span className="label">Horas Festivas:</span>
              <span className="value">
                {datosProcessados.reduce((sum, fila) => sum + fila.horasFestivas, 0).toFixed(2)}h
              </span>
            </div>
            <div className="resumen-item">
              <span className="label">Horas Nocturnas:</span>
              <span className="value">
                {datosProcessados.reduce((sum, fila) => sum + fila.horasNocturnas, 0).toFixed(2)}h
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CuadranteDataGrid;

